/**
 * Custom hook for article field autocomplete (brands, families, types, barcode types)
 * 
 * Provides debounced autocomplete suggestions from Elasticsearch
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v1Api } from '../../../api/generatedClient'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import type { APINamedAutocompleteResponse } from '../../../generated-types/models/APINamedAutocompleteResponse'
import type {
  V1SearchItemsArticleTypesAutocompleteRetrieveRequest,
  V1SearchItemsBarcodeTypesAutocompleteRetrieveRequest,
  V1SearchItemsBrandsAutocompleteRetrieveRequest,
  V1SearchItemsFamiliesAutocompleteRetrieveRequest,
} from '../../../generated-types/apis/V1Api'

export type ArticleFieldType = 'barcode-types' | 'article-types' | 'families' | 'brands'

export interface AutocompleteSuggestion {
  id: string
  name: string
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[]
  total: number
}

/**
 * Fetches autocomplete suggestions for a specific article field
 */
const fetchAutocomplete = async (
  fieldType: ArticleFieldType,
  query: string,
  size = 10
): Promise<AutocompleteResponse> => {
  const request = { q: query || undefined, size }

  let response: APINamedAutocompleteResponse

  switch (fieldType) {
    case 'barcode-types':
      response = await v1Api.v1SearchItemsBarcodeTypesAutocompleteRetrieve(
        request as V1SearchItemsBarcodeTypesAutocompleteRetrieveRequest
      )
      break
    case 'article-types':
      response = await v1Api.v1SearchItemsArticleTypesAutocompleteRetrieve(
        request as V1SearchItemsArticleTypesAutocompleteRetrieveRequest
      )
      break
    case 'families':
      response = await v1Api.v1SearchItemsFamiliesAutocompleteRetrieve(
        request as V1SearchItemsFamiliesAutocompleteRetrieveRequest
      )
      break
    case 'brands':
      response = await v1Api.v1SearchItemsBrandsAutocompleteRetrieve(
        request as V1SearchItemsBrandsAutocompleteRetrieveRequest
      )
      break
    default:
      throw new Error(`Unsupported field type: ${fieldType}`)
  }

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
    })),
    total: response.total,
  }
}

/**
 * Hook for article field autocomplete
 * 
 * @param fieldType - Type of field to autocomplete (barcode-types, article-types, families, brands)
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = useArticleFieldAutocomplete('brands')
 * 
 * // Update query on input change
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * 
 * // Display suggestions
 * {suggestions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
 * ```
 */
export function useArticleFieldAutocomplete(
  fieldType: ArticleFieldType,
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  // Debounce the query with 300ms delay
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<AutocompleteResponse, Error>({
    queryKey: ['article-field-autocomplete', fieldType, debouncedQuery],
    queryFn: () => fetchAutocomplete(fieldType, debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''), // Allow empty query for popular items
    staleTime: 30000, // Cache for 30 seconds
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


